import { useState } from 'react'
import {
  Drawer,
  Stack,
  Text,
  Badge,
  Button,
  Group,
  Modal,
  Textarea,
  Code,
  Skeleton,
  Alert,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { IconAlertCircle } from '@tabler/icons-react'
import { databaseApi, type DbRecommendation } from '@/api/database'
import { Callout } from '@/components/ui'

const SEVERITY_COLOR: Record<string, string> = {
  critical: 'red',
  high: 'orange',
  medium: 'yellow',
  low: 'blue',
}

const STATUS_COLOR: Record<string, string> = {
  open: 'blue',
  applied: 'green',
  dismissed: 'gray',
}

interface Props {
  rec: DbRecommendation | null
  tenantId: number
  onClose: () => void
}

export function RecommendationDetail({ rec, tenantId, onClose }: Props) {
  const queryClient = useQueryClient()
  const [dismissOpen, setDismissOpen] = useState(false)
  const [dismissReason, setDismissReason] = useState('')

  const detailQuery = useQuery({
    queryKey: ['rds-rec-detail', tenantId, rec?.db_identifier, rec?.id],
    queryFn: () => databaseApi.getRecommendation(tenantId, rec!.db_identifier, rec!.id),
    enabled: !!rec,
    staleTime: 30_000,
  })

  const applyMutation = useMutation({
    mutationFn: () => databaseApi.applyRecommendation(tenantId, rec!.db_identifier, rec!.id),
    onSuccess: (data) => {
      if (data.status === 'approval_pending') {
        notifications.show({
          title: 'Aprovação pendente',
          message: 'Solicitação de manutenção criada',
          color: 'blue',
        })
      } else {
        notifications.show({
          title: 'Aplicada',
          message: 'Recomendação aplicada com sucesso',
          color: 'green',
        })
      }
      queryClient.invalidateQueries({ queryKey: ['rds-recommendations', tenantId, rec!.db_identifier] })
      queryClient.invalidateQueries({ queryKey: ['rds-rec-detail', tenantId, rec!.db_identifier, rec!.id] })
      onClose()
    },
    onError: () => {
      notifications.show({ title: 'Erro', message: 'Falha ao aplicar recomendação', color: 'red' })
    },
  })

  const dismissMutation = useMutation({
    mutationFn: (reason: string) =>
      databaseApi.dismissRecommendation(tenantId, rec!.db_identifier, rec!.id, reason),
    onSuccess: () => {
      notifications.show({
        title: 'Dispensada',
        message: 'Recomendação dispensada',
        color: 'gray',
      })
      queryClient.invalidateQueries({ queryKey: ['rds-recommendations', tenantId, rec!.db_identifier] })
      setDismissOpen(false)
      setDismissReason('')
      onClose()
    },
    onError: () => {
      notifications.show({ title: 'Erro', message: 'Falha ao dispensar recomendação', color: 'red' })
    },
  })

  const detail = detailQuery.data
  const isOpen = rec?.status === 'open'

  return (
    <>
      <Drawer
        opened={!!rec}
        onClose={onClose}
        title="Detalhes da Recomendação"
        position="right"
        size="lg"
      >
        {detailQuery.isLoading ? (
          <Stack gap="sm">
            <Skeleton height={24} />
            <Skeleton height={16} />
            <Skeleton height={16} />
            <Skeleton height={80} />
          </Stack>
        ) : detailQuery.isError ? (
          <Alert color="red" icon={<IconAlertCircle size={16} />} title="Erro">
            Falha ao carregar detalhes da recomendação
          </Alert>
        ) : detail ? (
          <Stack gap="md">
            <Text size="lg" fw={600}>{detail.title}</Text>

            <Group gap="sm">
              <Badge color={SEVERITY_COLOR[detail.severity] ?? 'gray'}>{detail.severity}</Badge>
              <Badge color={STATUS_COLOR[detail.status] ?? 'gray'}>{detail.status}</Badge>
              <Badge variant="light">{detail.type}</Badge>
            </Group>

            <div>
              <Text size="sm" c="dimmed" fw={500}>Descrição</Text>
              <Text size="sm">{detail.description}</Text>
            </div>

            {detail.estimated_impact && (
              <div>
                <Text size="sm" c="dimmed" fw={500}>Impacto Estimado</Text>
                <Text size="sm">{detail.estimated_impact}</Text>
              </div>
            )}

            {detail.sql_to_apply && (
              <div>
                <Text size="sm" c="dimmed" fw={500}>SQL</Text>
                <Code block>{detail.sql_to_apply}</Code>
              </div>
            )}

            <div>
              <Text size="sm" c="dimmed" fw={500}>Criado em</Text>
              <Text size="sm">{new Date(detail.created_at).toLocaleString('pt-BR')}</Text>
            </div>

            {applyMutation.data?.status === 'approval_pending' && (
              <Callout variant="info" title="Aprovação pendente">
                <Text size="sm">Solicitação de manutenção criada</Text>
              </Callout>
            )}

            <Group gap="sm" mt="md">
              <Button
                size="sm"
                disabled={!isOpen}
                loading={applyMutation.isPending}
                onClick={() => applyMutation.mutate()}
              >
                Aplicar
              </Button>
              <Button
                size="sm"
                variant="subtle"
                disabled={!isOpen}
                onClick={() => setDismissOpen(true)}
              >
                Dispensar
              </Button>
            </Group>
          </Stack>
        ) : null}
      </Drawer>

      <Modal
        opened={dismissOpen}
        onClose={() => setDismissOpen(false)}
        title="Dispensar Recomendação"
        size="sm"
      >
        <Stack gap="sm">
          <Textarea
            label="Motivo"
            placeholder="Informe o motivo para dispensar"
            value={dismissReason}
            onChange={(e) => setDismissReason(e.currentTarget.value)}
            minRows={3}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDismissOpen(false)}>
              Cancelar
            </Button>
            <Button
              color="red"
              disabled={!dismissReason.trim()}
              loading={dismissMutation.isPending}
              onClick={() => dismissMutation.mutate(dismissReason.trim())}
            >
              Confirmar
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  )
}
